import React, { useState, useEffect } from 'react';
import Modal from '../../components/Modal';
import { urlMap, getAuthHeaders } from '../../../utils';

/**
 * MoveIssueToChapterModal — lets users move a chapter_issue to a different chapter.
 *
 * Flow:
 *  1. User sees list of chapters (excluding current)
 *  2. Clicking a chapter fetches & shows its summary
 *  3. User confirms or cancels the transfer
 *
 * Props:
 *  - isOpen: boolean
 *  - onClose: () => void
 *  - issue: object (chapter_issue row)
 *  - projectId: string
 *  - chapters: array of chapter objects
 *  - onMoved: (updatedIssue) => void
 */
export default function MoveIssueToChapterModal({ isOpen, onClose, issue, projectId, chapters, onMoved, initialChapterId = null }) {
  const [selectedChapter, setSelectedChapter] = useState(null);
  const [summary, setSummary] = useState(null);
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [summaryError, setSummaryError] = useState(null);
  const [moving, setMoving] = useState(false);
  const [moveError, setMoveError] = useState(null);

  // Reset state when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      setSelectedChapter(null);
      setSummary(null);
      setLoadingSummary(false);
      setSummaryError(null);
      setMoving(false);
      setMoveError(null);
    }
  }, [isOpen]);

  const availableChapters = (chapters || [])
    .filter((ch) => (ch.status || (ch.archived_at ? 'archived' : 'active')) === 'active')
    .sort((a, b) => (a.chapter_number ?? a.order_no ?? 0) - (b.chapter_number ?? b.order_no ?? 0));

  const handleSelectChapter = async (chapter) => {
    setSelectedChapter(chapter);
    setSummary(null);
    setSummaryError(null);
    setMoveError(null);
    setLoadingSummary(true);
    try {
      const res = await fetch(urlMap.getChapterSummary(chapter.id), {
        headers: { ...getAuthHeaders() },
        credentials: 'include',
      });
      if (res.status === 404) {
        setSummary(null);
        setSummaryError(null);
      } else if (!res.ok) {
        throw new Error(`HTTP ${res.status}`);
      } else {
        const data = await res.json();
        setSummary(data.summary || null);
      }
    } catch {
      setSummaryError('Failed to load chapter summary');
    } finally {
      setLoadingSummary(false);
    }
  };

  useEffect(() => {
    if (!isOpen || !initialChapterId) return;
    if (!availableChapters.length) return;
    const initialChapter = availableChapters.find((ch) => ch.id === initialChapterId);
    if (!initialChapter) return;
    if (selectedChapter?.id === initialChapter.id) return;
    handleSelectChapter(initialChapter);
  }, [isOpen, initialChapterId, availableChapters, selectedChapter]);

  const handleConfirm = async () => {
    if (!selectedChapter || !issue || !projectId) return;
    setMoving(true);
    setMoveError(null);
    try {
      const isStructural = issue._source === 'structural' || issue._source === 'thematic';
      const endpoint = isStructural
        ? urlMap.moveStructuralIssueToChapter(projectId, issue.id)
        : (issue.chapter_id
          ? urlMap.reassignChapterIssue(issue.chapter_id, issue.id)
          : urlMap.reassignIssue(projectId, issue.id));
      const res = await fetch(endpoint, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        credentials: 'include',
        body: JSON.stringify({ target_chapter_id: selectedChapter.id }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const updated = await res.json();
      // Structural issues are converted into chapter_issues on the backend.
      onMoved?.({ ...updated, _source: isStructural ? 'chapter' : (issue._source || 'chapter') });
      onClose();
    } catch {
      setMoveError('Failed to move issue. Please try again.');
    } finally {
      setMoving(false);
    }
  };

  const handleBack = () => {
    setSelectedChapter(null);
    setSummary(null);
    setSummaryError(null);
    setMoveError(null);
  };

  if (!issue) return null;

  const isStructural = issue._source === 'structural' || issue._source === 'thematic';
  const currentLabel = isStructural
    ? (issue._source === 'thematic' ? 'Project (thematic)' : 'Project (structural)')
    : issue.chapter_id
      ? `Ch ${issue.chapter_number ?? '?'}`
      : 'Project (unassigned)';

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <div className="move-issue-modal">
        <h3 className="move-issue-modal__title">Move Issue to Chapter</h3>
        <p className="move-issue-modal__subtitle">
          <span className="move-issue-modal__issue-name">{issue.title || 'Untitled issue'}</span>
          <span className="move-issue-modal__current">Currently in: {currentLabel}</span>
        </p>

        {!selectedChapter ? (
          /* ── Chapter list ── */
          <div className="move-issue-modal__list">
            {availableChapters.length === 0 ? (
              <p className="move-issue-modal__empty">No chapters available.</p>
            ) : (
              availableChapters.map((ch) => {
                const isCurrent = ch.id === issue?.chapter_id;
                return (
                  <button
                    key={ch.id}
                    type="button"
                    className={`move-issue-modal__chapter-btn${isCurrent ? ' move-issue-modal__chapter-btn--current' : ''}`}
                    onClick={() => handleSelectChapter(ch)}
                  >
                    <span className="move-issue-modal__chapter-num">Ch {ch.chapter_number ?? ch.order_no}</span>
                    <span className="move-issue-modal__chapter-title">{ch.title || 'Untitled'}</span>
                    {isCurrent && <span className="move-issue-modal__current-badge">current</span>}
                    <span className="move-issue-modal__chapter-arrow">→</span>
                  </button>
                );
              })
            )}
          </div>
        ) : (
          /* ── Summary preview + confirm ── */
          <div className="move-issue-modal__preview">
            <div className="move-issue-modal__preview-header">
              <button type="button" className="move-issue-modal__back-btn" onClick={handleBack}>
                ← Back
              </button>
              <span className="move-issue-modal__preview-label">
                Ch {selectedChapter.chapter_number ?? selectedChapter.order_no}
                {selectedChapter.title ? ` — ${selectedChapter.title}` : ''}
              </span>
            </div>

            <div className="move-issue-modal__summary-box">
              <label className="move-issue-modal__summary-label">Chapter Summary</label>
              {loadingSummary && (
                <div className="move-issue-modal__loading">
                  <div className="agent-spinner" aria-hidden="true" />
                  <span>Loading summary…</span>
                </div>
              )}
              {summaryError && (
                <p className="move-issue-modal__error">{summaryError}</p>
              )}
              {!loadingSummary && !summaryError && summary && (
                <p className="move-issue-modal__summary-text">{summary}</p>
              )}
              {!loadingSummary && !summaryError && !summary && (
                <p className="move-issue-modal__no-summary">No summary available for this chapter.</p>
              )}
            </div>

            {moveError && <p className="move-issue-modal__error">{moveError}</p>}

            <div className="move-issue-modal__actions">
              <button
                type="button"
                className="move-issue-modal__cancel-btn"
                onClick={onClose}
                disabled={moving}
              >
                Cancel
              </button>
              <button
                type="button"
                className="move-issue-modal__confirm-btn"
                onClick={handleConfirm}
                disabled={moving || loadingSummary}
              >
                {moving ? 'Moving…' : 'Confirm Move'}
              </button>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}
